import { axiosWithHeaders } from '@innovyze/lib_get_service';
import config, { apiEnv } from './config.api';

const api = axiosWithHeaders({
  service: 'amso' as any,
  config,
  environmentOverride: apiEnv,
  axiosCreateOpts: {
    headers: { 'Content-Type': 'application/json' },
    timeout: 300_000,
  },
});

export function getTenantId(): string | null {
  return sessionStorage.getItem('tenantId');
}

// --------------- Discovery ---------------

export interface DeteriorationModel {
  id: string;
  name: string;
  system_type?: string;
  asset_type?: string;
  asset_group_set_id?: string;
  trained_at?: string;
  run_at?: string;
}

export interface CostTable {
  id: string;
  name: string;
  cost_per?: string;
  asset_group_set_id?: string;
  compatible?: boolean;
}

export interface RiskConfig {
  id: string;
  config_id?: string;
  name: string;
  system_type?: string;
  asset_type?: string;
  risk_method?: string;
  compatible?: boolean;
}

export interface AssetFilterOptions {
  asset_types: string[];
  system_types: string[];
  materials: string[];
}

export async function listDeteriorationModels(
  tenantId: string,
): Promise<DeteriorationModel[]> {
  const { data } = await api.get(`/tenants/${tenantId}/deterioration-models`);
  return data;
}

export async function listCostTables(
  tenantId: string,
  assetGroupSetId?: string,
): Promise<CostTable[]> {
  const params: Record<string, string> = {};
  if (assetGroupSetId) params.asset_group_set_id = assetGroupSetId;
  const { data } = await api.get(`/tenants/${tenantId}/cost-tables`, {
    params,
  });
  return data;
}

export async function listRiskConfigs(
  tenantId: string,
  systemType?: string,
): Promise<RiskConfig[]> {
  const params: Record<string, string> = {};
  if (systemType) params.system_type = systemType;
  const { data } = await api.get(`/tenants/${tenantId}/risk-configs`, {
    params,
  });
  return data;
}

export async function listAssetFilters(
  tenantId: string,
): Promise<AssetFilterOptions> {
  const { data } = await api.get(`/tenants/${tenantId}/asset-filters`);
  return data;
}

// --------------- Run Config Types ---------------

export interface StrategyOverride {
  unit_cost_per_foot?: number;
  condition_improvement?: number;
  cost_escalation_rate?: number;
  disruption_days?: number;
  spatial_discount_max?: number;
}

export interface RunConfigPayload {
  name?: string;
  tenant_id: string;
  deterioration_model_id?: string;
  cost_table_id?: string;
  risk_config_id?: string;
  asset_filters?: {
    system_type?: string;
    asset_type?: string;
    materials?: string[];
  };
  planning_horizon: number;
  annual_budget: number;
  tier: number;
  primary_objective: string;

  n_pareto_points?: number;
  population_size?: number;
  n_generations?: number;
  failure_penalty?: number;
  max_network_disruption_pct?: number;
  early_stop_patience?: number;

  condition_failure?: number;
  condition_critical?: number;
  condition_poor?: number;

  strategies?: {
    do_nothing?: StrategyOverride;
    spot_repair?: StrategyOverride;
    cipp_section_lining?: StrategyOverride;
    replace?: StrategyOverride;
  };

  grouping_method?: string;
  spatial_weight?: number;
  max_radius_km?: number;
  min_group_size?: number;
  max_group_size?: number;
  compact_threshold_km?: number;

  spatial_compact_threshold_km?: number;
  cost_model?: string;

  solver_time_limit?: number;
  mip_rel_gap?: number;

  inspection_enabled?: boolean;
  inspection_min_structural_grade?: number;
  inspection_min_operational_grade?: number;
  trench_length_per_point_defect_ft?: number;
  trench_buffer_ft?: number;
  trench_merge_gap_ft?: number;
  freshness_full_confidence_years?: number;
  freshness_reduced_confidence_years?: number;
  freshness_low_confidence_years?: number;
  stale_data_condition_gap?: number;
  intervention_thresholds?: {
    point_repair_max_fraction: number;
    section_lining_max_fraction: number;
    rehabilitation_max_fraction: number;
  };
  repair_to_replace_threshold?: number;

  cof_estimation_enabled?: boolean;
  cof_gis_weight?: number;
  cof_esri_config?: {
    buffer_meters: number;
    facility_layers: {
      hospitals: { weight: number };
      schools: { weight: number };
      fire_stations: { weight: number };
    };
  };
  cof_proxy_weights?: {
    diameter: number;
    material: number;
    subtype: number;
    length: number;
  };
  cof_critical_assets?: string[];
  default_consequence?: number;
}

// --------------- Runs ---------------

export interface RunResponse {
  run_id: string;
  name?: string;
  status: string;
  total_assets?: number;
  summary?: Record<string, unknown>;
}

export interface RunSummary {
  run_id: string;
  name?: string;
  tenant_id: string;
  tier: number;
  status: string;
  total_assets?: number;
  n_solutions?: number;
  solve_time?: number;
  created_at: string;
  completed_at?: string;
}

export interface RunDetail extends RunSummary {
  deterioration_model_id?: string;
  cost_table_id?: string;
  risk_config_id?: string;
  planning_horizon: number;
  annual_budget: number;
  primary_objective: string;
  error_message?: string;
  grouping_info?: Record<string, unknown>;
  analysis?: Record<string, unknown>;
  convergence?: Record<string, unknown>;
  config?: Record<string, unknown>;
}

export interface YearlySpendingEntry {
  year: number;
  cost: number;
  strategy?: string;
}

export interface SolutionSummary {
  solution_index: number;
  risk?: number;
  budget_utilization?: number;
  condition_improvement?: number;
  yearly_spending?: YearlySpendingEntry[];
  metrics?: Record<string, unknown>;
}

export interface ArtifactSummary {
  id: number;
  artifact_type: string;
  filename: string;
  solution_index?: number | null;
  created_at: string;
}

export interface ValidationResult {
  valid: boolean;
  warnings: string[];
  errors: string[];
}

export async function validateRun(
  payload: RunConfigPayload,
): Promise<ValidationResult> {
  const { data } = await api.post('/runs/validate', payload);
  return data;
}

export async function submitRun(
  payload: RunConfigPayload,
): Promise<RunResponse> {
  const { data } = await api.post('/runs', payload);
  return data;
}

export async function listRuns(
  tenantId?: string,
  limit = 50,
): Promise<RunSummary[]> {
  const params: Record<string, string | number> = { limit };
  if (tenantId) params.tenant_id = tenantId;
  const { data } = await api.get('/runs', { params });
  return data;
}

export async function deleteRun(runId: string): Promise<void> {
  await api.delete(`/runs/${runId}`);
}

export async function getRunDetail(runId: string): Promise<RunDetail> {
  const { data } = await api.get(`/runs/${runId}`);
  return data;
}

export async function getRunSolutions(
  runId: string,
): Promise<SolutionSummary[]> {
  const { data } = await api.get(`/runs/${runId}/solutions`);
  return data;
}

export async function getRunArtifacts(
  runId: string,
): Promise<ArtifactSummary[]> {
  const { data } = await api.get(`/runs/${runId}/artifacts`);
  return data;
}

export interface GroupingDetail {
  group_statistics: Record<string, any>[];
  spatial_summary: Record<string, any>[];
}

export async function getRunGrouping(runId: string): Promise<GroupingDetail> {
  const { data } = await api.get(`/runs/${runId}/grouping`);
  return data;
}

export interface ConditionTrajectories {
  horizon: number;
  baseline: Record<string, number[]>;
  solution: Record<string, number[]>;
}

export async function getConditionTrajectories(
  runId: string,
  solutionIndex: number,
): Promise<ConditionTrajectories> {
  const { data } = await api.get(`/runs/${runId}/condition-trajectories`, {
    params: { solution_index: solutionIndex },
  });
  return data;
}

// --------------- Strategy Names ---------------

const STRATEGY_NAMES: Record<number, string> = {
  1: 'Recommended Plan',
  2: 'Budget Analysis',
  3: 'Strategy Explorer',
};

export function strategyName(tier: number): string {
  return STRATEGY_NAMES[tier] ?? `Strategy ${tier}`;
}

export const STRATEGY_OPTIONS = [
  { value: 1, label: 'Recommended Plan' },
  { value: 2, label: 'Budget Analysis' },
  { value: 3, label: 'Strategy Explorer' },
];

export const STRATEGY_DESCRIPTIONS: Record<number, string> = {
  1: 'Finds one recommended plan that makes the best use of your budget. Takes longer to run but gives you a single, ready-to-implement answer.',
  2: 'Shows how risk reduction changes as you spend more. Reveals where additional spending stops paying off, helping you decide how much to invest.',
  3: 'Explores many different combinations of repair strategies at once. May find creative approaches \u2014 like cheaper repairs on more assets \u2014 that other methods miss. Results are approximate but diverse.',
};

export function getArtifactDownloadUrl(
  runId: string,
  artifactId: number,
): string {
  return `${api.defaults.baseURL}/runs/${runId}/artifacts/${artifactId}`;
}

export async function downloadArtifact(
  runId: string,
  artifactId: number,
): Promise<void> {
  const url = getArtifactDownloadUrl(runId, artifactId);
  const { data, headers } = await api.get(url, {
    responseType: 'blob',
    baseURL: '',
  });
  const disposition = headers['content-disposition'] || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : 'download';
  const a = document.createElement('a');
  a.href = URL.createObjectURL(data);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

export async function downloadCmmsWorkOrders(
  runId: string,
  solutionIndex: number,
  cmmsType: string,
): Promise<void> {
  const { data, headers } = await api.get(
    `/runs/${runId}/solutions/${solutionIndex}/export-work-orders`,
    { params: { cmms_type: cmmsType }, responseType: 'blob' },
  );
  const disposition = headers['content-disposition'] || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : `work_orders_${cmmsType}.csv`;
  const a = document.createElement('a');
  a.href = URL.createObjectURL(data);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}
